import { useEffect, useMemo, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import toast from "react-hot-toast";
import { brutoCapex, netoDesdeBrutoCapex } from "../../utils/brutoCapex";
import {
  getConceptoCostoLabel,
  getCostosProyectoConRemanentePorProveedor,
  vincularAnticipoCosto,
} from "../../utils/costosProyecto";
import { useCurrency } from "../../utils/useCurrency";

// Paso OPCIONAL, DESPUÉS de confirmar un "A cuenta → Proyecto (sin factura)" en Banco
// (VincularMovimientoModal, 2026-10-04). El pago YA está registrado (categoria_pago='pago_a_cuenta',
// RPC vincular_caja_pura — ver index.js) antes de que este modal se abra: si el usuario cierra sin
// elegir nada, o la RPC de abajo rechaza algo, el pago queda igual, registrado — nunca se pierde. La
// vinculación a un costo de Cartelería siempre se puede hacer después desde "Vincular pago ya hecho"
// en la ventana Costos de Cartelería (CarteleriaCostosModal/VincularAnticipoCostoModal.js, dirección
// inversa de este modal: ese parte del costo y lista pagos; este parte del pago y lista costos).
//
// Dirección inversa de VincularAnticipoCostoModal (10.1: mismo cálculo bruto↔neto, `netoDesdeBrutoCapex`/
// `brutoCapex`; misma RPC única `vincular_anticipo_costo`, Fase 5 de Cartelería, 20261004b — escrita,
// pendiente de que Ignacio la aplique). Mientras esa migración no esté aplicada, el botón "Vincular"
// de este modal falla con el error real de Postgres (10.177, sin camuflarlo) — el pago en Banco ya
// quedó registrado igual.
const VincularAnticipoProyectoModal = ({
  show,
  pais,
  proveedorId,
  proveedorNombre,
  movimientoCajaId,
  montoBrutoDisponible,
  usuarioId,
  onHide,
  onVinculado,
}) => {
  const [cargando, setCargando] = useState(true);
  const [costos, setCostos] = useState([]);
  const [seleccion, setSeleccion] = useState(""); // costo_id (string)
  const [montoBruto, setMontoBruto] = useState("");
  const [guardando, setGuardando] = useState(false);

  const { formatAmount } = useCurrency();

  useEffect(() => {
    if (!show) return;
    let vivo = true;
    setCargando(true);
    setSeleccion("");
    setMontoBruto("");
    (async () => {
      try {
        const candidatos = await getCostosProyectoConRemanentePorProveedor({ pais, proveedorId });
        if (!vivo) return;
        setCostos(candidatos);
      } catch (e) {
        toast.error(`Error al cargar líneas de costo: ${e.message || e}`);
      } finally {
        if (vivo) setCargando(false);
      }
    })();
    return () => {
      vivo = false;
    };
  }, [show, pais, proveedorId]);

  const costoSeleccionado = useMemo(() => costos.find((c) => String(c.id) === seleccion) || null, [costos, seleccion]);

  // Remanente de la línea medido en BRUTO (la caja es siempre bruto, regla 6 de Fase 5) — tope de
  // referencia para el default del input; el tope REAL de validación lo hace la RPC en neto y en
  // bruto por movimiento.
  const remanenteLineaBruto = useMemo(() => {
    if (!costoSeleccionado) return null;
    return brutoCapex(Math.max(0, costoSeleccionado.remanenteNeto), {
      exento_iva: costoSeleccionado.exentoIva,
      moneda: costoSeleccionado.moneda,
      pais,
    });
  }, [costoSeleccionado, pais]);

  const handleSeleccionar = (value) => {
    setSeleccion(value);
    const c = costos.find((x) => String(x.id) === value);
    if (c) {
      const brutoLinea = brutoCapex(Math.max(0, c.remanenteNeto), { exento_iva: c.exentoIva, moneda: c.moneda, pais });
      setMontoBruto(String(Math.max(0, Math.min(brutoLinea, Number(montoBrutoDisponible || 0)))));
    }
  };

  const montoNetoCalculado = useMemo(() => {
    if (!montoBruto || !costoSeleccionado) return 0;
    return netoDesdeBrutoCapex(Number(montoBruto), {
      exento_iva: costoSeleccionado.exentoIva,
      moneda: costoSeleccionado.moneda,
      pais,
    });
  }, [montoBruto, costoSeleccionado, pais]);

  const handleGuardar = async () => {
    if (!costoSeleccionado) return toast.error("Seleccioná una línea de costo");
    const bruto = Number(montoBruto);
    if (!bruto || bruto <= 0) return toast.error("El monto a vincular debe ser mayor a 0");
    setGuardando(true);
    try {
      const resultado = await vincularAnticipoCosto({
        costoId: costoSeleccionado.id,
        movimientoCajaId,
        montoNeto: montoNetoCalculado,
        montoBruto: bruto,
        usuarioId: Number.isFinite(Number(usuarioId)) ? Number(usuarioId) : null,
      });
      toast.success("Pago vinculado como anticipo del proyecto");
      onVinculado?.(resultado);
    } catch (err) {
      // 10.177: el mensaje real de la RPC (candados de proveedor/país/categoría/topes, o "función no
      // existe" si Ignacio todavía no aplicó la migración de Fase 5), tal cual — nunca camuflado.
      toast.error("Error al vincular el anticipo: " + (err.message || err));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>¿Corresponde a un proyecto de Cartelería?</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div style={{ fontSize: "0.8rem", color: "#6c757d", marginBottom: 12 }}>
          El pago a cuenta de <strong>{proveedorNombre || "este proveedor"}</strong> ya quedó registrado. Si corresponde
          a un costo de proyecto de Cartelería (implementación, pantalla, soporte…) todavía sin factura, podés
          vincularlo ahora como anticipo — o hacerlo más tarde desde la ventana Costos de Cartelería, "Vincular pago ya
          hecho".
        </div>

        {cargando ? (
          <div style={{ textAlign: "center", padding: 20 }}>Cargando…</div>
        ) : costos.length === 0 ? (
          <div style={{ color: "#999", textAlign: "center", padding: 20 }}>
            No hay líneas de costo de Cartelería vigentes de este proveedor con remanente pendiente.
          </div>
        ) : (
          <>
            <Form.Group className="mb-3">
              <Form.Label>Línea de costo</Form.Label>
              <Form.Select value={seleccion} onChange={(e) => handleSeleccionar(e.target.value)}>
                <option value="">Seleccioná una línea…</option>
                {costos.map((c) => (
                  <option key={c.id} value={String(c.id)}>
                    {c.nombreProyecto || `Proyecto #${c.negocioId}`} — {getConceptoCostoLabel(c.concepto)} · remanente{" "}
                    {formatAmount(c.remanenteNeto, c.moneda)} (neto)
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {costoSeleccionado && (
              <Form.Group className="mb-3">
                <Form.Label>Monto a vincular (bruto — lo que salió por caja)</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  value={montoBruto}
                  onChange={(e) => setMontoBruto(e.target.value)}
                />
                <Form.Text className="text-muted">
                  Equivalente neto: {formatAmount(montoNetoCalculado, costoSeleccionado.moneda)}
                  {remanenteLineaBruto != null &&
                    ` · remanente de la línea en bruto: ${formatAmount(remanenteLineaBruto, costoSeleccionado.moneda)}`}
                </Form.Text>
              </Form.Group>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onHide} disabled={guardando}>
          {costos.length === 0 ? "Cerrar" : "Omitir"}
        </Button>
        {costos.length > 0 && (
          <Button variant="primary" size="sm" onClick={handleGuardar} disabled={guardando || !costoSeleccionado}>
            {guardando ? "Vinculando…" : "Vincular"}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default VincularAnticipoProyectoModal;
